import React, { useEffect, useMemo, useState } from 'react';
import { 
  Users, 
  Clock, 
  TrendingUp, 
  Building2, 
  FileText,
  Activity,
  Zap,
  Lightbulb,
  AlertCircle,
  MapPin,
  Settings,
  User
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { ultronTuringApi } from '../../services/ultronTuringApi';

interface DeviceZone {
  id: string;
  name: string;
  devices: string[];
}

interface Device {
  deviceId: string;
  baseSN: string;
  deviceType: string;
  displayName?: string;
  name?: string;
  nickname?: string;
  deviceSN?: string;
  sn?: string;
  isOn?: boolean;
  presence?: boolean;
  occupancy?: boolean;
  occupied?: boolean;
  detected?: boolean;
  motionDetected?: boolean;
  traits?: string[];
  attributes?: any;
}

interface OccupancyAnalyticsViewProps {
  devices: Device[];
  zones: DeviceZone[];
  onOpenZoneSelector?: () => void;
  selectedZoneCount?: number;
}

interface ZoneStat {
  id: string;
  name: string;
  isOccupied: boolean;
  lastActivity: number | null;
  hasConfiguredSensors: boolean;
}

// Demo zone data for live monitor
const initialDemoZones: ZoneStat[] = [
  { id: 'demo-1', name: 'Floor 1 - Lobby', isOccupied: true, lastActivity: null, hasConfiguredSensors: false },
  { id: 'demo-2', name: 'Floor 1 - Conference A', isOccupied: true, lastActivity: null, hasConfiguredSensors: false },
  { id: 'demo-3', name: 'Floor 2 - Open Office', isOccupied: true, lastActivity: null, hasConfiguredSensors: false },
  { id: 'demo-4', name: 'Floor 2 - Break Room', isOccupied: false, lastActivity: 60, hasConfiguredSensors: false },
  { id: 'demo-5', name: 'Floor 3 - Executive', isOccupied: true, lastActivity: null, hasConfiguredSensors: false },
  { id: 'demo-6', name: 'Floor 3 - Server Room', isOccupied: false, lastActivity: 120, hasConfiguredSensors: false },
];

const HOUR_MS = 60 * 60 * 1000;

const OccupancyAnalyticsView: React.FC<OccupancyAnalyticsViewProps> = ({ 
  devices, 
  zones,
  onOpenZoneSelector,
  selectedZoneCount
}) => {
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');
  
  const [hourlyData, setHourlyData] = useState<Array<{ time: string; presenceRate: number | null }>>([]);
  const [weeklyData, setWeeklyData] = useState<Array<{ day: string; avg: number | null; peak: number | null }>>([]);
  
  const PRESENCE_ZONE_SENSOR_MAP_KEY = 'occupancy-zone-presence-sensors';
  const [zonePresenceSensorMap, setZonePresenceSensorMap] = useState<Record<string, string[]>>(() => {
    try {
      const saved = localStorage.getItem(PRESENCE_ZONE_SENSOR_MAP_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showPresenceSensorModal, setShowPresenceSensorModal] = useState(false);
  const [selectedZoneForPresence, setSelectedZoneForPresence] = useState<string | null>(null);
  const [tempSelectedPresenceSensors, setTempSelectedPresenceSensors] = useState<string[]>([]);
  const [presenceSearch, setPresenceSearch] = useState('');
  const [draftZonePresenceSensorMap, setDraftZonePresenceSensorMap] = useState<Record<string, string[]>>({});

  // Filter presence sensors
  const presenceSensors = useMemo(() => 
    devices.filter(d => 
      d.deviceType === 'presence_sensor' || 
      d.deviceType === 'motion_sensor' ||
      d.traits?.some(t => ['OccupancySensing', 'MotionSensing'].includes(t))
    ), [devices]);

  const getPresenceState = (d: Device) => {
    const attrs = d.attributes || {};
    const values = attrs.values || {};
    const detectedValue =
      values.detected?.value ??
      attrs.detected ??
      attrs.motionDetected ??
      attrs.motion ??
      attrs.presenceDetected ??
      attrs.occupancyDetected;
    const detected =
      Array.isArray(detectedValue)
        ? detectedValue.includes('presenceDetected') ||
          detectedValue.includes('motionDetected') ||
          detectedValue.includes('occupancy') ||
          detectedValue.includes(true)
        : typeof detectedValue === 'string'
          ? detectedValue === 'presenceDetected' ||
            detectedValue === 'motionDetected' ||
            detectedValue === 'occupancy'
          : Boolean(detectedValue);
    const occupancyValue =
      d.occupancy ??
      values.occupancy?.value ??
      values.presence?.value ??
      attrs.occupancy ??
      attrs.presence;
    return Boolean(
      occupancyValue ??
      d.presence ??
      d.occupied ??
      d.motionDetected ??
      d.detected ??
      detected ??
      d.isOn ??
      false
    );
  };

  const presenceSensorInfo = useMemo(() => presenceSensors.map(d => ({
    id: d.deviceId,
    name: d.nickname || d.displayName || d.name || d.deviceId,
    deviceSN: d.deviceSN || d.sn || d.deviceId,
    baseSN: d.baseSN,
    presence: getPresenceState(d),
  })), [presenceSensors]);

  const openPresenceSensorModal = () => {
    if (!zones.length) return;
    const zoneId = selectedZoneForPresence || zones[0].id;
    const draft = { ...zonePresenceSensorMap };
    setDraftZonePresenceSensorMap(draft);
    setSelectedZoneForPresence(zoneId);
    setTempSelectedPresenceSensors(draft[zoneId] || []);
    setShowPresenceSensorModal(true);
  };

  useEffect(() => {
    let isCancelled = false;
    const fetchPresenceAnalytics = async () => {
      const mappedIds = Object.values(zonePresenceSensorMap).flat().map(id => String(id));
      const targetSensors = presenceSensors.filter(d =>
        mappedIds.includes(d.deviceId) || (d.baseSN && mappedIds.includes(d.baseSN))
      );
      const currentZonePercent = (() => {
        const zoneStats = zones.map(zone => {
          const mappedSensors = zonePresenceSensorMap[zone.id] || [];
          const zoneSensors = mappedSensors.length > 0
            ? presenceSensors.filter(d => mappedSensors.includes(d.deviceId) || mappedSensors.includes(d.baseSN))
            : [];
          const occupied = zoneSensors.some(d => getPresenceState(d));
          return { occupied };
        });
        const total = zones.length;
        const occupiedCount = zoneStats.filter(z => z.occupied).length;
        return total > 0 ? Math.round((occupiedCount / total) * 100) : null;
      })();
      const deviceIds = Array.from(new Set(
        targetSensors
          .map(d => d.baseSN || d.deviceSN || d.deviceId)
          .filter(Boolean)
          .map(id => String(id).split('#')[0])
      ));
      const mapKey = JSON.stringify(zonePresenceSensorMap || {});
      const cacheKey = `week:${deviceIds.sort().join(',')}:${mapKey}`;
      const CACHE_KEY = 'occupancy-analytics-cache';
      const CACHE_TTL_MS = 60 * 60 * 1000;

      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.key === cacheKey && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
            if (!isCancelled) {
              const cachedHourly = Array.isArray(cached.hourlyData) ? [...cached.hourlyData] : [];
              if (currentZonePercent !== null) {
                const now = new Date();
                const currentHour = now.getHours();
                cachedHourly[currentHour] = {
                  time: cachedHourly[currentHour]?.time || `${currentHour.toString().padStart(2, '0')}:00`,
                  presenceRate: currentZonePercent
                };
              }
              setHourlyData(cachedHourly);
              setWeeklyData(cached.weeklyData || []);
            }
            return;
          }
        }
      } catch {}

      const now = new Date();
      const currentHour = now.getHours();

      const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const todayIdx = (now.getDay() + 6) % 7; // 0 = Mon
      const weekStart = new Date(now);
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(weekStart.getDate() - todayIdx);

      if (deviceIds.length === 0) {
        const hourlyEmpty = Array.from({ length: 24 }, (_, hour) => ({
          time: `${hour.toString().padStart(2, '0')}:00`,
          presenceRate: hour > currentHour ? null : 0,
        }));
        const weeklyEmpty = dayLabels.map((day, i) => ({
          day,
          avg: i > todayIdx ? null : 0,
          peak: i > todayIdx ? null : 0,
        }));
        if (!isCancelled) {
          setHourlyData(hourlyEmpty);
          setWeeklyData(weeklyEmpty);
        }
        return;
      }

      const parseEventTimestamp = (event: any): number | null => {
        if (typeof event?.timestamp === 'number') return event.timestamp;
        if (event?.time) {
          const parsed = new Date(event.time).getTime();
          return Number.isNaN(parsed) ? null : parsed;
        }
        if (event?.timeStamp) {
          const parsed = new Date(event.timeStamp).getTime();
          return Number.isNaN(parsed) ? null : parsed;
        }
        return null;
      };

      const parseOccupancyValue = (event: any): boolean | null => {
        const raw = event?.message?.occupancy ??
          event?.details?.actionValue ??
          event?.details?.occupancy ??
          event?.actionValue ??
          event?.event ??
          event?.message?.event;
        if (raw === true || raw === 'true' || raw === 1) return true;
        if (raw === false || raw === 'false' || raw === 0) return false;
        if (raw === '有人') return true;
        if (raw === '無人') return false;
        return null;
      };

      const deviceEventsMap = new Map<string, Array<{ time: number; occupied: boolean }>>();
      const deviceTimePatternsMap = new Map<string, {
        hourly: Record<string, number>;
        weekly: Record<string, number>;
        hourlyMax: number;
        weeklyMax: number;
      }>();
      const sensorKeyById = new Map<string, string>();
      presenceSensors.forEach(d => {
        const key = String(d.baseSN || d.deviceSN || d.deviceId).split('#')[0];
        sensorKeyById.set(d.deviceId, key);
        if (d.baseSN) sensorKeyById.set(d.baseSN, key);
      });
      await Promise.all(deviceIds.map(async deviceId => {
        try {
          const result = await ultronTuringApi.analyzeDeviceLogs(deviceId, {
            period: 'week',
            format: 'detailed'
          });
          const recentEvents = (result?.data as any)?.recentEvents || [];
          const timePatterns = (result?.data as any)?.analysis?.timePatterns || {};
          const hourlyDistribution = timePatterns?.hourlyDistribution || {};
          const weeklyDistribution = timePatterns?.weeklyDistribution || {};
          const hourlyValues = Object.values(hourlyDistribution).filter(v => typeof v === 'number') as number[];
          const weeklyValues = Object.values(weeklyDistribution).filter(v => typeof v === 'number') as number[];
          deviceTimePatternsMap.set(deviceId, {
            hourly: hourlyDistribution,
            weekly: weeklyDistribution,
            hourlyMax: hourlyValues.length ? Math.max(...hourlyValues) : 0,
            weeklyMax: weeklyValues.length ? Math.max(...weeklyValues) : 0
          });
          const events: Array<{ time: number; occupied: boolean }> = [];
          for (const event of recentEvents) {
            const ts = parseEventTimestamp(event);
            const occupied = parseOccupancyValue(event);
            if (ts !== null && occupied !== null) {
              events.push({ time: ts, occupied });
            }
          }
          events.sort((a, b) => a.time - b.time);
          deviceEventsMap.set(deviceId, events);
        } catch {
          deviceEventsMap.set(deviceId, []);
          deviceTimePatternsMap.set(deviceId, { hourly: {}, weekly: {}, hourlyMax: 0, weeklyMax: 0 });
        }
      }));

      const buildHourlyStates = (events: Array<{ time: number; occupied: boolean }>, startMs: number, hours: number) => {
        const states: Array<boolean | null> = new Array(hours).fill(null);
        let idx = 0;
        let currentState: boolean | null = null;
        while (idx < events.length && events[idx].time < startMs) {
          currentState = events[idx].occupied;
          idx += 1;
        }
        for (let h = 0; h < hours; h += 1) {
          const hourEnd = startMs + (h + 1) * HOUR_MS - 1;
          while (idx < events.length && events[idx].time <= hourEnd) {
            currentState = events[idx].occupied;
            idx += 1;
          }
          states[h] = currentState;
        }
        return states;
      };
      const sensorStateCache = new Map<string, Array<boolean | null>>();
      const getSensorScore = (sensorKey: string, dayStartMs: number, hour: number): number | null => {
        const cacheKey = `${sensorKey}:${dayStartMs}`;
        if (!sensorStateCache.has(cacheKey)) {
          const events = deviceEventsMap.get(sensorKey) || [];
          sensorStateCache.set(cacheKey, buildHourlyStates(events, dayStartMs, 24));
        }
        const states = sensorStateCache.get(cacheKey) || [];
        const state = states[hour];
        if (state === true) return 1;
        if (state === false) return 0;
        const patterns = deviceTimePatternsMap.get(sensorKey);
        const raw = patterns?.hourly?.[String(hour)] ?? patterns?.hourly?.[hour];
        if (typeof raw === 'number' && patterns?.hourlyMax) {
          return raw / patterns.hourlyMax;
        }
        return null;
      };

      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayStartMs = todayStart.getTime();

      const hourlyRates: Array<{ time: string; presenceRate: number | null }> = [];
      for (let hour = 0; hour < 24; hour += 1) {
        const label = `${hour.toString().padStart(2, '0')}:00`;
        if (hour > currentHour) {
          hourlyRates.push({ time: label, presenceRate: null });
          continue;
        }
        const totalZones = zones.length;
        let zoneScoreSum = 0;
        zones.forEach(zone => {
          const mappedSensors = zonePresenceSensorMap[zone.id] || [];
          if (mappedSensors.length === 0) {
            return;
          }
          let zoneScore = 0;
          let hasScore = false;
          mappedSensors.forEach(sensorId => {
            const key = sensorKeyById.get(sensorId) || String(sensorId).split('#')[0];
            const score = getSensorScore(key, todayStartMs, hour);
            if (score !== null) {
              zoneScore = Math.max(zoneScore, score);
              hasScore = true;
            }
          });
          zoneScoreSum += hasScore ? zoneScore : 0;
        });
        const rate = totalZones > 0 ? Math.round((zoneScoreSum / totalZones) * 100) : null;
        hourlyRates.push({ time: label, presenceRate: rate });
      }

      if (currentZonePercent !== null) {
        hourlyRates[currentHour] = {
          time: hourlyRates[currentHour]?.time || `${currentHour.toString().padStart(2, '0')}:00`,
          presenceRate: currentZonePercent
        };
      }

      const weeklyRates: Array<{ day: string; avg: number | null; peak: number | null }> = [];
      for (let dayIdx = 0; dayIdx < 7; dayIdx += 1) {
        if (dayIdx > todayIdx) {
          weeklyRates.push({ day: dayLabels[dayIdx], avg: null, peak: null });
          continue;
        }
        const dayStart = new Date(weekStart);
        dayStart.setDate(dayStart.getDate() + dayIdx);
        const dayStartMs = dayStart.getTime();
        const dayHourlyRates: number[] = [];
        const dayScores: number[] = [];
        for (let hour = 0; hour < 24; hour += 1) {
          if (dayIdx === todayIdx && hour > currentHour) {
            continue;
          }
          const totalZones = zones.length;
          let zoneScoreSum = 0;
          zones.forEach(zone => {
            const mappedSensors = zonePresenceSensorMap[zone.id] || [];
            if (mappedSensors.length === 0) {
              return;
            }
            let zoneScore = 0;
            let hasScore = false;
            mappedSensors.forEach(sensorId => {
              const key = sensorKeyById.get(sensorId) || String(sensorId).split('#')[0];
              const score = getSensorScore(key, dayStartMs, hour);
              if (score !== null) {
                zoneScore = Math.max(zoneScore, score);
                hasScore = true;
              }
            });
            zoneScoreSum += hasScore ? zoneScore : 0;
          });
          const rate = totalZones > 0 ? Math.round((zoneScoreSum / totalZones) * 100) : 0;
          dayHourlyRates.push(rate);
          dayScores.push(rate);
        }
        const avg = dayHourlyRates.length > 0
          ? Math.round(dayHourlyRates.reduce((sum, v) => sum + v, 0) / dayHourlyRates.length)
          : 0;
        const peak = dayHourlyRates.length > 0 ? Math.max(...dayHourlyRates) : 0;
        weeklyRates.push({ day: dayLabels[dayIdx], avg, peak });
      }

      if (!isCancelled) {
        setHourlyData(hourlyRates);
        setWeeklyData(weeklyRates);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            key: cacheKey,
            fetchedAt: Date.now(),
            hourlyData: hourlyRates,
            weeklyData: weeklyRates
          }));
        } catch {}
      }
    };

    fetchPresenceAnalytics();
    return () => {
      isCancelled = true;
    };
  }, [presenceSensors, zonePresenceSensorMap, zones]);

  
  // Calculate zone stats with demo data
  const zoneStats = useMemo<ZoneStat[]>(() => {
    if (zones.length === 0) {
      return initialDemoZones;
    }
    
      return zones.map((zone, index) => {
      const mappedSensors = zonePresenceSensorMap[zone.id] || [];
      const zoneSensors = mappedSensors.length > 0
        ? presenceSensors.filter(d => mappedSensors.includes(d.deviceId) || mappedSensors.includes(d.baseSN))
        : [];
      const occupiedCount = zoneSensors.filter(d => getPresenceState(d)).length;
      
      return {
        id: zone.id,
        name: zone.name,
          isOccupied: occupiedCount > 0,
          hasConfiguredSensors: true,
          lastActivity: null,
      };
    });
  }, [zones, presenceSensors, zonePresenceSensorMap]);
  
  // Calculate summary stats
  const activeCount = zoneStats.filter(z => z.isOccupied).length;
  const inactiveCount = zoneStats.filter(z => !z.isOccupied).length;
  const totalZones = zoneStats.length;
  const activePercent = totalZones > 0 ? Math.round((activeCount / totalZones) * 100) : 0;

  const zoneDistributionData = useMemo(() => {
    if (totalZones === 0) return [];
    return [
      { name: '有人', value: activePercent, color: '#10b981' },
      { name: '無人', value: 100 - activePercent, color: '#64748b' },
    ];
  }, [activePercent, totalZones]);

  const floorBreakdownData = useMemo(() => {
    const byFloor = new Map<string, { total: number; active: number }>();
    zoneStats.forEach(zone => {
      const match = zone.name.match(/Floor\\s*(\\d+)/i) || zone.name.match(/(\\d+)F/i);
      const floorLabel = match ? `Floor ${match[1]}` : 'Floor 1';
      const current = byFloor.get(floorLabel) || { total: 0, active: 0 };
      current.total += 1;
      if (zone.isOccupied) current.active += 1;
      byFloor.set(floorLabel, current);
    });
    return Array.from(byFloor.entries()).map(([floor, stats]) => {
      const percentage = stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0;
      const status = percentage > 60 ? 'high' : percentage > 30 ? 'moderate' : 'low';
      return { floor, zones: stats.total, active: stats.active, percentage, status };
    });
  }, [zoneStats]);

  return (
    <div className="space-y-4">
      {/* Zone Controls Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="w-4 h-4 text-slate-400" />
          <div>
            <h3 className="text-base font-semibold text-white">Zone Controls</h3>
            <p className="text-sm text-slate-500">Presence zone management</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-400">{activeCount} 有人</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-slate-800/300" />
            <span className="text-slate-400">{inactiveCount} 無人</span>
          </span>
          <button
            onClick={openPresenceSensorModal}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
            title="設定 Presence Sensor"
          >
            <Settings className="w-3 h-3" />
            <span>感應器</span>
          </button>
          {onOpenZoneSelector && (
            <button
              onClick={onOpenZoneSelector}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
              title="選擇區域"
            >
              <Settings className="w-3 h-3" />
              <span>區域</span>
              {typeof selectedZoneCount === 'number' && (
                <span className="text-slate-500">({selectedZoneCount})</span>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Charts Row - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Occupancy */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-cyan-400" />
            <h3 className="text-base font-semibold text-white">Hourly Presence</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">Presence rate throughout the day</p>
          
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hourlyData}>
                <defs>
                  <linearGradient id="occupancyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="time" 
                  ticks={['00:00','04:00','08:00','12:00','16:00','20:00']}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#334155' }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #475569', 
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#f1f5f9'
                  }}
                  itemStyle={{ color: '#f1f5f9' }}
                  labelStyle={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}
                  formatter={(value: any) => [value === null ? '--' : `${value}%`, 'Presence']}
                />
                <Area
                  type="monotone"
                  dataKey="presenceRate"
                  stroke="#06b6d4"
                  strokeWidth={2}
                  fill="url(#occupancyGradient)"
                  name="Presence"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Weekly Trend */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <h3 className="text-base font-semibold text-white">Weekly Trend</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">Average vs peak presence rate by day</p>
          
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData} barGap={2}>
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={{ stroke: '#334155' }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #475569', 
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#f1f5f9'
                  }}
                  itemStyle={{ color: '#f1f5f9' }}
                  labelStyle={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}
                  formatter={(value, name) => {
                    const displayValue = value == null ? '--' : `${value}%`;
                    return [displayValue, typeof name === 'string' ? name : ''];
                  }}
                />
                <Bar dataKey="avg" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Average" />
                <Bar dataKey="peak" fill="#10b981" radius={[4, 4, 0, 0]} name="Peak" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Zone Distribution & Floor Breakdown - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Zone Distribution */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-blue-400" />
            <h3 className="text-base font-semibold text-white">Presence Distribution</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">Presence share by zone</p>
          
          <div className="flex items-center gap-6">
            <div className="h-44 w-44 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={zoneDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {zoneDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #475569', 
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#f1f5f9'
                    }}
                    itemStyle={{ color: '#f1f5f9' }}
                    labelStyle={{ color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}
                    formatter={(value) => {
                      const displayValue = typeof value === 'number' ? `${value}%` : '--';
                      return [displayValue, 'Presence'];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="flex-1 space-y-3">
              {zoneDistributionData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-base text-slate-300">{item.name}</span>
                  </div>
                  <span className="text-base font-medium text-white">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Floor Breakdown */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-amber-400" />
            <h3 className="text-base font-semibold text-white">Floor Presence</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">Current presence by floor</p>
          
          <div className="space-y-4">
            {floorBreakdownData.map((floor, index) => {
              const statusColors = {
                low: { bg: 'bg-emerald-500', text: 'text-emerald-400' },
                moderate: { bg: 'bg-amber-500', text: 'text-amber-400' },
                high: { bg: 'bg-red-500', text: 'text-red-400' },
              } as const;
              const colors = statusColors[floor.status as keyof typeof statusColors];
              
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-base font-medium text-white">{floor.floor}</span>
                      <span className="text-sm text-slate-500">{floor.zones} zones</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium capitalize ${colors.text}`}>
                        {floor.status}
                      </span>
                      <span className="text-base text-slate-400">
                        {floor.percentage}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors.bg} rounded-full transition-all`}
                      style={{ width: `${floor.percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Live Zone Monitor Section */}
      <div className="bg-[#0C2340] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-cyan-400" />
            <div>
              <h3 className="text-base font-semibold text-white">Live Zone Monitor</h3>
              <p className="text-sm text-slate-500">Real-time presence by zone</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-slate-400">{activeCount} 有人</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-500" />
              <span className="text-slate-400">{inactiveCount} 無人</span>
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zoneStats.map((zone) => {
            const isOccupied = zone.isOccupied;
            const hasSensors = zone.hasConfiguredSensors;
            
            return (
              <div
                key={zone.id}
                className={`rounded-lg p-4 border transition-all ${
                  zone.isOccupied 
                    ? 'bg-[#050d18] border-slate-600' 
                    : 'bg-[#050d18] border-slate-700/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-4 h-4 ${zone.isOccupied ? 'text-cyan-400' : 'text-slate-500'}`} />
                    <span className={`text-base font-medium ${zone.isOccupied ? 'text-white' : 'text-slate-500'}`}>
                      {zone.name}
                    </span>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    !hasSensors ? 'bg-slate-700' : zone.isOccupied ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'
                  }`} />
                </div>
                
                {!hasSensors ? (
                  <p className="text-sm text-slate-500">
                    本區沒有配置感應器
                  </p>
                ) : zone.isOccupied ? (
                  <p className={`text-xl font-bold mb-2 ${isOccupied ? 'text-emerald-400' : 'text-slate-500'}`}>
                    有人
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 mb-1">無人</p>
                    {zone.lastActivity !== null && (
                      <p className="text-xs text-slate-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last activity: {zone.lastActivity}m ago
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bottom Insights Row - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Peak Hours Insight */}
        <div className="bg-[#0C2340] rounded-xl p-4 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h4 className="text-base font-semibold text-white">Peak Hours Insight</h4>
          </div>
          <p className="text-base text-slate-400">
            Highest presence rate occurs between 10 AM - 2 PM. Consider staggered lunch schedules.
          </p>
        </div>
        
        {/* Underutilized Space */}
        <div className="bg-[#0C2340] rounded-xl p-4 border-l-4 border-l-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-blue-400" />
            <h4 className="text-base font-semibold text-white">Underutilized Space</h4>
          </div>
          <p className="text-base text-slate-400">
            Conference Room A averages low presence rate. Consider desk conversion.
          </p>
        </div>
        
        {/* Energy Opportunity */}
        <div className="bg-[#0C2340] rounded-xl p-4 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-emerald-400" />
            <h4 className="text-base font-semibold text-white">Energy Opportunity</h4>
          </div>
          <p className="text-base text-slate-400">
            Floor 3 shows low weekend presence. Recommend HVAC scheduling optimization.
          </p>
        </div>
      </div>

      {showPresenceSensorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPresenceSensorModal(false)}>
          <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-500" />
                Zone Presence Sensors
              </h3>
              <button onClick={() => setShowPresenceSensorModal(false)} className="text-slate-400 hover:text-slate-200">
                ✕
              </button>
            </div>

            <div className="flex h-[70vh]">
              <div className="w-1/3 border-r border-slate-700/30 p-4 overflow-y-auto">
                <h4 className="text-sm text-slate-400 mb-3">選擇區域</h4>
                <div className="space-y-2">
                  {zones.map(zone => (
                    <button
                      key={zone.id}
                      onClick={() => {
                        setSelectedZoneForPresence(zone.id);
                        setTempSelectedPresenceSensors(draftZonePresenceSensorMap[zone.id] || []);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        selectedZoneForPresence === zone.id
                          ? 'bg-cyan-600/20 border-cyan-400/60 text-white shadow-[0_0_0_1px_rgba(34,211,238,0.35)]'
                          : 'bg-slate-800/50 border-slate-700/40 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      <div className="text-sm font-medium truncate">{zone.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 p-4 flex flex-col overflow-hidden">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    value={presenceSearch}
                    onChange={e => setPresenceSearch(e.target.value)}
                    placeholder="搜尋 Presence Sensor"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {presenceSensorInfo
                    .filter(s => {
                      if (!presenceSearch) return true;
                      const search = presenceSearch.toLowerCase();
                      return s.name.toLowerCase().includes(search) || s.id.toLowerCase().includes(search);
                    })
                    .map(sensor => (
                      <div
                        key={sensor.id}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                          tempSelectedPresenceSensors.includes(sensor.id)
                            ? 'bg-cyan-600/20 border border-cyan-400/60 shadow-[0_0_0_1px_rgba(34,211,238,0.35)]'
                            : 'bg-slate-800/50 border border-slate-700/40 hover:bg-slate-700/50'
                        }`}
                        onClick={() => {
                          const currentZoneId = selectedZoneForPresence;
                          if (!currentZoneId) return;
                          setTempSelectedPresenceSensors(prev => {
                            const next = prev.includes(sensor.id)
                              ? prev.filter(id => id !== sensor.id)
                              : [...prev, sensor.id];
                            setDraftZonePresenceSensorMap(map => ({ ...map, [currentZoneId]: next }));
                            return next;
                          });
                        }}
                      >
                        <div>
                          <div className="text-sm text-white">{sensor.name}</div>
                          <div className="text-xs text-slate-500">{sensor.deviceSN}</div>
                        </div>
                        <User className={`w-4 h-4 ${sensor.presence ? 'text-red-400' : 'text-emerald-400'}`} />
                      </div>
                    ))}
                </div>
                <div className="pt-3 mt-3 border-t border-slate-700/30 flex items-center justify-between">
                  <div className="text-xs text-slate-400">
                    {tempSelectedPresenceSensors.length} 已選擇
                  </div>
                  <button
                    onClick={() => {
                      if (!selectedZoneForPresence) return;
                      setZonePresenceSensorMap(prev => {
                        const next = {
                          ...prev,
                          ...draftZonePresenceSensorMap,
                          [selectedZoneForPresence]: tempSelectedPresenceSensors
                        };
                        try {
                          localStorage.setItem(PRESENCE_ZONE_SENSOR_MAP_KEY, JSON.stringify(next));
                        } catch {}
                        return next;
                      });
                      setShowPresenceSensorModal(false);
                    }}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
                  >
                    儲存
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OccupancyAnalyticsView;
